/**
 * Reaction toggle hook with optimistic UI.
 *
 *   const { toggle, add, remove, isPending, error } = useReaction()
 *   await toggle(conversationId, messageId, '👍')
 *
 * Flow (add):
 *   1. Optimistic `applyReactionAdded(convId, msgId, myId, emoji, true)`
 *      — store is **caller-aware idempotent** so the channel echo lands
 *      as a no-op.
 *   2. POST `messengerApi.addReaction(messageId, { emoji })`.
 *   3. Failure → `applyReactionRemoved(convId, msgId, myId, emoji, true)`
 *      undoes the optimistic flip.
 *
 * Same shape for remove.
 *
 * `toggle` looks up the message's current `byMe` aggregate and dispatches
 * to `add` or `remove` accordingly. UI only needs to pass the emoji.
 */

import { useCallback, useState } from 'react'
import { useAuthStore } from '@/features/auth/store/auth.store'
import { reactionOnConversation } from '../services/messenger.socket'
import { useMessengerStore } from '../store/messenger.store'

interface UseReaction {
  toggle: (conversationId: string, messageId: string, emoji: string) => Promise<void>
  add:    (conversationId: string, messageId: string, emoji: string) => Promise<void>
  remove: (conversationId: string, messageId: string, emoji: string) => Promise<void>
  isPending: boolean
  error: string | null
}

export function useReaction(): UseReaction {
  const me = useAuthStore((s) => s.user)
  // The reaction's "who" is keyed by contactPresenceId in the store — the channel
  // echo carries the server's contactPresenceId, so the optimistic write must use
  // the same id to reconcile (byMe + de-dupe). Socket push stays `me.id`.
  const presenceId = useAuthStore((s) => s.presenceId)
  const applyAdded   = useMessengerStore((s) => s.applyReactionAdded)
  const applyRemoved = useMessengerStore((s) => s.applyReactionRemoved)

  const [isPending, setIsPending] = useState<boolean>(false)
  const [error, setError]         = useState<string | null>(null)

  const add = useCallback(
    async (conversationId: string, messageId: string, emoji: string): Promise<void> => {
      if (!me?.id || !presenceId) { setError('not authenticated'); return }
      setIsPending(true)
      setError(null)

      // Optimistic — idempotent for the caller path, so a races channel echo
      // is a no-op.
      applyAdded(conversationId, messageId, presenceId, emoji, true)
      try {
        await reactionOnConversation(me.id, conversationId, messageId, emoji, 'add', me.name)
      } catch (err: unknown) {
        applyRemoved(conversationId, messageId, presenceId, emoji, true)
        setError(err instanceof Error ? err.message : 'failed to add reaction')
      } finally {
        setIsPending(false)
      }
    },
    [me?.id, me?.name, presenceId, applyAdded, applyRemoved],
  )

  const remove = useCallback(
    async (conversationId: string, messageId: string, emoji: string): Promise<void> => {
      if (!me?.id || !presenceId) { setError('not authenticated'); return }
      setIsPending(true)
      setError(null)

      applyRemoved(conversationId, messageId, presenceId, emoji, true)
      try {
        await reactionOnConversation(me.id, conversationId, messageId, emoji, 'remove', me.name)
      } catch (err: unknown) {
        applyAdded(conversationId, messageId, presenceId, emoji, true)
        setError(err instanceof Error ? err.message : 'failed to remove reaction')
      } finally {
        setIsPending(false)
      }
    },
    [me?.id, me?.name, presenceId, applyAdded, applyRemoved],
  )

  const toggle = useCallback(
    async (conversationId: string, messageId: string, emoji: string): Promise<void> => {
      const list = useMessengerStore.getState().wireMessagesByConv.get(conversationId)
      const msg  = list?.find((m) => m.id === messageId)
      const myAggregate = msg?.reactions?.find((r) => r.emoji === emoji)
      const currentlyByMe = myAggregate?.byMe ?? false
      if (currentlyByMe) {
        await remove(conversationId, messageId, emoji)
      } else {
        await add(conversationId, messageId, emoji)
      }
    },
    [add, remove],
  )

  return { toggle, add, remove, isPending, error }
}
